/* eslint-disable react-hooks/exhaustive-deps, no-unused-vars */
import { useState, useEffect } from 'react'
import { supabase } from './supabase'
import Auth from './components/Auth'
import Dashboard from './components/Dashboard'
import Qualification from './components/Qualification'
import BidPlan from './components/BidPlan'
import Opportunities from './components/Opportunities'
import Artefacts from './components/Artefacts'
import AtRiskPlans from './components/AtRiskPlans'
import OrgSetup from './components/OrgSetup'
import UserManagement from './components/UserManagement'
import BrandingSettings from './components/BrandingSettings'
import ActionTracker from './components/ActionTracker'
import QuestionTracker from './components/QuestionTracker'
import LicenceSettings from './components/LicenceSettings'
import OrganisationSettings from './components/OrganisationSettings'
import OrgManagement from './components/OrgManagement'
import FrameworkLibrary from './components/FrameworkLibrary'
import CompetitorLibrary from './components/CompetitorLibrary'
import TcVarianceAnalysis from './components/TcVarianceAnalysis'
import OpportunityIntelligence from './components/OpportunityIntelligence'
import OpportunityCompetitors from './components/OpportunityCompetitors'
import ReportingSuite from './components/ReportingSuite'
import ClarificationQuestions from './components/ClarificationQuestions'
import AllActions from './components/AllActions'
import ChangePassword from './components/ChangePassword'

function App() {
  const [session, setSession] = useState(null)
  const [loading, setLoading] = useState(true)
  const [currentPage, setCurrentPage] = useState(() => localStorage.getItem('cadence_page') || 'dashboard')
  const [bidPlanOpp, setBidPlanOpp] = useState(null)
  const [artefactsOpp, setArtefactsOpp] = useState(null)
  const [actionsOpp, setActionsOpp] = useState(null)
  const [questionsOpp, setQuestionsOpp] = useState(null)
  const [oppInitialFilter, setOppInitialFilter] = useState(null)
  const [cqOpp, setCqOpp] = useState(null)
  const [atRiskOpps, setAtRiskOpps] = useState([])
  const [frameworkId, setFrameworkId] = useState(null)
  const [competitorId, setCompetitorId] = useState(null)
  const [competitorOpp, setCompetitorOpp] = useState(null)
  const [tcOpp, setTcOpp] = useState(null)
  const [profile, setProfile] = useState(null)
  const [orgUsers, setOrgUsers] = useState([])
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [intelligenceOpen, setIntelligenceOpen] = useState(false)

  useEffect(() => {
    // Pages that should not be restored on refresh (require context)
    const noRestore = ['cqs', 'actions', 'questions', 'artefacts', 'competitors', 'bidplan']
    if (!noRestore.includes(currentPage)) {
      localStorage.setItem('cadence_page', currentPage)
    }
  }, [currentPage])
  const [allOrgs, setAllOrgs] = useState([])
  const [activeOrg, setActiveOrg] = useState(() => { try { const s = localStorage.getItem('cadence_active_org'); return s ? JSON.parse(s) : null } catch { return null } })

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session)
      if (session) loadProfile(session.user.id)
      setLoading(false)
    })
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session)
      if (session) loadProfile(session.user.id)
    })
    return () => subscription.unsubscribe()
  }, [])
  // eslint-disable-next-line react-hooks/exhaustive-deps

  const loadAllOrgs = async () => {
    const { data } = await supabase
      .from('organisations')
      .select('id, name, slug, primary_colour, secondary_colour, logo_url, powered_by_cadence, licensed_user_count, licence_active')
      .order('name')
    setAllOrgs(data || [])
  }

  const loadProfile = async (userId) => {
    const { data } = await supabase
      .from('profiles')
      .select('username, full_name, role, organisation_id, organisations(id, name, slug, primary_colour, secondary_colour, logo_url, powered_by_cadence, licence_tier, ai_enabled, button_colour, header_colour, accent_colour)')
      .eq('id', userId)
      .single()
    if (data) {
      setProfile(data)
      if (data.organisation_id) {
        const { data: users } = await supabase
          .from('profiles')
          .select('id, username, full_name')
          .eq('organisation_id', data.organisation_id)
          .eq('is_active', true)
          .order('full_name')
        setOrgUsers(users || [])
      }
      if (data.role === 'super_admin') {
        loadAllOrgs()
      }
    }
  }

  const switchOrg = async (org) => {
    setActiveOrg(org)
    localStorage.setItem('cadence_active_org', JSON.stringify(org))
    setCurrentPage('dashboard')
    const { data: users } = await supabase
      .from('profiles')
      .select('id, username, full_name')
      .eq('organisation_id', org.id)
      .eq('is_active', true)
      .order('full_name')
    setOrgUsers(users || [])
  }

  const returnToOwnOrg = () => {
    setActiveOrg(null)
    localStorage.removeItem('cadence_active_org')
    setCurrentPage('dashboard')
    loadProfile(session.user.id)
  }

  const effectiveProfile = activeOrg
    ? { ...profile, organisation_id: activeOrg.id, organisations: activeOrg }
    : profile


  // Inject org brand colours as CSS variables whenever org changes
  useEffect(() => {
  // eslint-disable-next-line react-hooks/exhaustive-deps
    const org = effectiveProfile?.organisations
    if (!org) return
    const root = document.documentElement
    root.style.setProperty('--nav-bg', '#' + (org.primary_colour || '1A3A2A'))
    console.log('CSS injected for:', org.name, org.primary_colour)
  }, [
    effectiveProfile?.organisations?.id,
    effectiveProfile?.organisations?.primary_colour,
    effectiveProfile?.organisations?.secondary_colour,
    effectiveProfile?.organisations?.button_colour,
    effectiveProfile?.organisations?.header_colour,
    effectiveProfile?.organisations?.accent_colour
  ])

  const handleOpenBidPlan = (opp, planId) => {
    setBidPlanOpp(planId ? { ...opp, planId } : opp)
    setCurrentPage('bidplan')
  }

  const handleOpenAtRiskPlan = (atRiskOpps) => {
    setAtRiskOpps(atRiskOpps)
    setCurrentPage('atrisk')
  }

  const handleOpenArtefacts = (opp) => {
    setArtefactsOpp(opp)
    setCurrentPage('artefacts')
  }

  const handleOpenActions = (opp) => {
    setActionsOpp(opp)
    setCurrentPage('actions')
  }

  const handleOpenQuestions = (opp) => {
    setQuestionsOpp(opp)
    setCurrentPage('questions')
  }

  const handleOpenTcAnalysis = (opp) => {
    setTcOpp(opp)
    setCurrentPage('tcanalysis')
  }

  const handleOpenCompetitors = (opp) => {
    setCompetitorOpp(opp)
    setCurrentPage('competitors')
  }

  const handleOpenFramework = (id) => {
    setFrameworkId(id)
    setCurrentPage('frameworks')
  }

  const handleOpenGlobalActions = () => {
  setActionsOpp(null)
  setCurrentPage('actions')
  }

  const handleOpenCqs = (opp) => {
    setCqOpp(opp)
    setCurrentPage('cqs')
  }

  if (loading) return null
  if (!session) return <Auth />
  if (session && profile !== null && !profile?.organisation_id) {
    return <OrgSetup session={session} onComplete={() => loadProfile(session.user.id)} />
  }

  return (
    <div onClick={() => { setSettingsOpen(false); setIntelligenceOpen(false) }}>
      <nav className="app-nav" style={{ background: effectiveProfile?.organisations?.primary_colour ? `#${effectiveProfile.organisations.primary_colour}` : undefined, borderBottomColor: effectiveProfile?.organisations?.primary_colour ? `#${effectiveProfile.organisations.primary_colour}` : undefined }}>
        <div className="app-nav__brand" onClick={() => setCurrentPage('dashboard')} style={{ cursor: 'pointer' }}>
          {effectiveProfile?.organisations?.logo_url ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
              <img
                src={effectiveProfile.organisations.logo_url}
                alt={effectiveProfile.organisations.name}
                style={{ height: '34px', maxWidth: '160px', objectFit: 'contain' }}
              />
              {effectiveProfile.organisations.powered_by_cadence && (
                <span style={{ fontSize: '0.5rem', opacity: 0.5, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--white)', marginTop: '2px' }}>
                  Powered by Cadence
                </span>
              )}
            </div>
          ) : (
            <>
              CADENCE <span style={{ fontSize: '0.6875rem', opacity: 0.6, letterSpacing: '0.08em' }}>by Captus</span>
            </>
          )}
        </div>
        <div className="app-nav__links">
          <button className={`app-nav__link ${currentPage === 'dashboard' ? 'active' : ''}`} onClick={() => setCurrentPage('dashboard')}>Dashboard</button>
          <button className={`app-nav__link ${currentPage === 'opportunities' ? 'active' : ''}`} onClick={() => setCurrentPage('opportunities')}>Pipeline</button>
          <button className={`app-nav__link ${currentPage === 'qualification' ? 'active' : ''}`} onClick={() => setCurrentPage('qualification')}>Qualify</button>
          <button className={`app-nav__link ${currentPage === 'bidplan' ? 'active' : ''}`} onClick={() => { setBidPlanOpp(null); setCurrentPage('bidplan') }}>Bid Plan</button>
          <button className={`app-nav__link ${currentPage === 'artefacts' ? 'active' : ''}`} onClick={() => setCurrentPage('artefacts')}>Artefacts</button>
          <button className={`app-nav__link ${currentPage === 'frameworks' ? 'active' : ''}`} onClick={() => { setFrameworkId(null); setCurrentPage('frameworks') }}>Frameworks</button>
          <div style={{ position: 'relative' }}>
            <button
              className={`app-nav__link ${currentPage === 'reporting' ? 'active' : ''}`}
              onClick={e => { e.stopPropagation(); setIntelligenceOpen(prev => !prev) }}
            >
              Intelligence {intelligenceOpen ? '▲' : '▾'}
            </button>
            {intelligenceOpen && (
              <div
                onClick={e => e.stopPropagation()}
                style={{ position: 'absolute', top: '100%', left: 0, background: 'var(--white)', border: '1px solid var(--green-light)', boxShadow: '0 8px 24px rgba(0,0,0,0.12)', minWidth: '220px', zIndex: 1000, marginTop: '4px' }}
              >
                <button
                  onClick={() => { setIntelligenceOpen(false); setCurrentPage('reporting') }}
                  style={{ display: 'block', width: '100%', textAlign: 'left', padding: '10px 16px', background: 'none', border: 'none', borderBottom: '1px solid var(--green-light)', fontSize: '0.875rem', color: 'var(--charcoal)', cursor: 'pointer', fontFamily: 'inherit', fontWeight: currentPage === 'reporting' ? 700 : 400 }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--green-pale)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'none'}
                >
                  Reporting and Analytics
                </button>
                {[
                  { label: 'Competitive Intelligence', page: 'competitive', active: true },
                  { label: 'Opportunity Intelligence', page: 'oppintel', active: true },

                  { label: 'Framework Intelligence', page: 'frameworks', active: true },
                  { label: 'T&C Variance Analysis', page: 'opportunities', active: true },
                ].map(item => (
                  <div key={item.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 16px', borderBottom: '1px solid var(--green-light)', opacity: item.active ? 1 : 0.5, cursor: item.active ? 'pointer' : 'default', background: 'none' }}
                    onClick={() => { if (item.active) { setIntelligenceOpen(false); setCurrentPage(item.page || 'dashboard') } }}
                    onMouseEnter={e => { if (item.active) e.currentTarget.style.background = 'var(--green-pale)' }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'none' }}
                  >
                    <span style={{ fontSize: '0.875rem', color: item.active ? 'var(--charcoal)' : 'var(--slate)', fontFamily: 'inherit' }}>{item.label}</span>
                    {!item.active && <span style={{ fontSize: '0.625rem', fontWeight: 600, color: 'var(--slate)', letterSpacing: '0.06em' }}>{item.note}</span>}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="app-nav__user">
          {activeOrg && (
            <div style={{ background: 'var(--red)', color: 'var(--white)', padding: '3px 10px', fontSize: '0.6875rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', marginRight: '8px' }}>
              Viewing: {activeOrg.name}
            </div>
          )}
          <span>{profile?.username || session.user.email}</span>
          <div style={{ position: 'relative' }}>
            <button className="app-nav__signout" onClick={(e) => { e.stopPropagation(); setSettingsOpen(prev => !prev) }}>
              ⚙ Settings
            </button>
            {settingsOpen && (
              <div onClick={e => e.stopPropagation()} style={{ position: 'absolute', top: '100%', right: 0, background: 'var(--white)', border: '1px solid var(--green-light)', boxShadow: '0 8px 24px rgba(0,0,0,0.12)', minWidth: '220px', zIndex: 1000, marginTop: '4px' }}>
                <button onClick={() => { setSettingsOpen(false); setCurrentPage('changepassword') }} style={{ display: 'block', width: '100%', textAlign: 'left', padding: '10px 16px', background: 'none', border: 'none', borderBottom: '1px solid var(--green-light)', fontSize: '0.875rem', color: 'var(--charcoal)', cursor: 'pointer', fontFamily: 'inherit' }} onMouseEnter={e => e.currentTarget.style.background = 'var(--green-pale)'} onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                  🔑 Change Password
                </button>
                {(profile?.role === 'super_admin' || profile?.role === 'admin') && (
                  <>
                    <div style={{ padding: '8px 16px', fontSize: '0.625rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--slate)', borderBottom: '1px solid var(--green-light)' }}>
                      Administration
                    </div>
                    <button onClick={() => { setSettingsOpen(false); setCurrentPage('orgsettings') }} style={{ display: 'block', width: '100%', textAlign: 'left', padding: '10px 16px', background: 'none', border: 'none', borderBottom: '1px solid var(--green-light)', fontSize: '0.875rem', color: 'var(--charcoal)', cursor: 'pointer', fontFamily: 'inherit' }} onMouseEnter={e => e.currentTarget.style.background = 'var(--green-pale)'} onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                      ⚙️ Organisation Settings
                    </button>
                  </>
                )}
                {profile?.role === 'super_admin' && (
                  <>
                    <button onClick={() => { setSettingsOpen(false); setCurrentPage('orgmanagement') }} style={{ display: 'block', width: '100%', textAlign: 'left', padding: '10px 16px', background: 'none', border: 'none', borderBottom: '1px solid var(--green-light)', fontSize: '0.875rem', color: 'var(--charcoal)', cursor: 'pointer', fontFamily: 'inherit' }} onMouseEnter={e => e.currentTarget.style.background = 'var(--green-pale)'} onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                      🏢 Organisation Management
                    </button>
                    <div style={{ padding: '8px 16px', fontSize: '0.625rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--slate)', borderBottom: '1px solid var(--green-light)' }}>
                      Switch Organisation
                    </div>
                    {allOrgs.map(org => (
                      <button key={org.id} onClick={() => { switchOrg(org); setSettingsOpen(false) }}
                        style={{ display: 'flex', alignItems: 'center', gap: '10px', width: '100%', textAlign: 'left', padding: '10px 16px', background: (activeOrg?.id === org.id || (!activeOrg && org.id === profile?.organisation_id)) ? 'var(--green-pale)' : 'none', border: 'none', borderBottom: '1px solid var(--green-light)', fontSize: '0.875rem', color: 'var(--charcoal)', cursor: 'pointer', fontFamily: 'inherit' }}
                        onMouseEnter={e => e.currentTarget.style.background = 'var(--green-pale)'}
                        onMouseLeave={e => e.currentTarget.style.background = (activeOrg?.id === org.id || (!activeOrg && org.id === profile?.organisation_id)) ? 'var(--green-pale)' : 'none'}
                      >
                        <div style={{ width: '8px', height: '8px', background: '#' + (org.primary_colour || '1A3A2A'), flexShrink: 0 }} />
                        <span>{org.name}</span>
                        {(activeOrg?.id === org.id || (!activeOrg && org.id === profile?.organisation_id)) && (
                          <span style={{ marginLeft: 'auto', fontSize: '0.625rem', color: 'var(--green)', fontWeight: 700 }}>✓</span>
                        )}
                      </button>
                    ))}
                    {activeOrg && (
                      <button onClick={() => { returnToOwnOrg(); setSettingsOpen(false) }}
                        style={{ display: 'block', width: '100%', textAlign: 'left', padding: '10px 16px', background: 'none', border: 'none', borderBottom: '1px solid var(--green-light)', fontSize: '0.875rem', color: 'var(--green-dark)', cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600 }}
                        onMouseEnter={e => e.currentTarget.style.background = 'var(--green-pale)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'none'}
                      >
                        ← Return to {profile?.organisations?.name}
                      </button>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
          <button className="app-nav__signout" onClick={() => supabase.auth.signOut()}>Sign Out</button>
        </div>
      </nav>
      <div className="app-content">
        {currentPage === 'dashboard' && (
          <Dashboard
            key={'dashboard' + (activeOrg?.id || '')}
            session={session}
            profile={effectiveProfile}
            onNavigate={setCurrentPage}
            onOpenActions={handleOpenGlobalActions}
            onOpenAtRiskPlan={handleOpenAtRiskPlan}
            openAllActions={() => {
    setActionsOpp(null)
    setCurrentPage('actions')
  }}
          />
        )}
        {currentPage === 'opportunities' && (
          <Opportunities
            key={oppInitialFilter || 'default'}
            session={session}
            profile={effectiveProfile}
            orgUsers={orgUsers}
            onNavigate={(page, filter) => { setCurrentPage(page); if (filter) setOppInitialFilter(filter) }}
            onOpenBidPlan={handleOpenBidPlan}
            onOpenArtefacts={handleOpenArtefacts}
            onOpenActions={handleOpenActions}
            onOpenQuestions={handleOpenQuestions}
            onOpenCqs={handleOpenCqs}
            onOpenFramework={handleOpenFramework}
            onOpenCompetitors={handleOpenCompetitors}
            onOpenTcAnalysis={handleOpenTcAnalysis}
            initialFilter={oppInitialFilter}
          />
        )}
        {currentPage === 'qualification' && (
          <Qualification
            session={session}
            profile={effectiveProfile}
            orgUsers={orgUsers}
            onNavigate={setCurrentPage}
          />
        )}
        {currentPage === 'bidplan' && (
          <BidPlan
            session={session}
            profile={effectiveProfile}
            onNavigate={setCurrentPage}
            linkedOpportunity={bidPlanOpp}
            onOpenArtefacts={handleOpenArtefacts}
            onOpenActions={handleOpenActions}
            onOpenQuestions={handleOpenQuestions}
            onOpenCqs={handleOpenCqs}
            onOpenFramework={handleOpenFramework}
          />
        )}
        {currentPage === 'atrisk' && (
          <AtRiskPlans
            session={session}
            onNavigate={setCurrentPage}
            atRiskOpps={atRiskOpps}
            onOpenBidPlan={handleOpenBidPlan}
          />
        )}
        {currentPage === 'questions' && questionsOpp && (
          <QuestionTracker
            opportunity={questionsOpp}
            profile={effectiveProfile}
            orgUsers={orgUsers}
            onClose={() => setCurrentPage('opportunities')}
          />
        )}
        {currentPage === 'cqs' && cqOpp && (
          <ClarificationQuestions
            opportunity={cqOpp}
            profile={effectiveProfile}
            onClose={() => setCurrentPage('opportunities')}
          />
        )}
       {currentPage === 'actions' && !actionsOpp && (
  <AllActions
    profile={effectiveProfile}
    orgUsers={orgUsers}
    onOpenAction={handleOpenActions}
  />
)}

{currentPage === 'actions' && actionsOpp && (
  <ActionTracker
    opportunity={actionsOpp}
    profile={effectiveProfile}
    orgUsers={orgUsers}
    onClose={() => {
      setActionsOpp(null)
      setCurrentPage('actions')
    }}
  />
)}
        {currentPage === 'artefacts' && (
          <Artefacts
            session={session}
            profile={effectiveProfile}
            onNavigate={setCurrentPage}
            linkedOpportunity={artefactsOpp}
          />
        )}
        {currentPage === 'users' && (
          <UserManagement
            session={session}
            profile={effectiveProfile}
            onNavigate={setCurrentPage}
          />
        )}
        {currentPage === 'tcanalysis' && tcOpp && (
          <TcVarianceAnalysis
            opportunity={tcOpp}
            profile={effectiveProfile}
            onClose={() => setCurrentPage('opportunities')}
          />
        )}
        {currentPage === 'competitive' && (
          <CompetitorLibrary
            session={session}
            profile={effectiveProfile}
            onNavigate={setCurrentPage}
            initialCompetitorId={competitorId}
          />
        )}
        {currentPage === 'competitors' && competitorOpp && (
          <OpportunityCompetitors
            session={session}
            profile={effectiveProfile}
            opportunity={competitorOpp}
            onClose={() => setCurrentPage('opportunities')}
          />
        )}
        {currentPage === 'oppintel' && (
          <OpportunityIntelligence
            session={session}
            profile={effectiveProfile}
            onNavigate={setCurrentPage}
          />
        )}
        {currentPage === 'reporting' && (
          <ReportingSuite
            session={session}
            profile={effectiveProfile}
            onNavigate={setCurrentPage}
          />
        )}
        {currentPage === 'frameworks' && (
          <FrameworkLibrary
            session={session}
            profile={effectiveProfile}
            onNavigate={setCurrentPage}
            initialFrameworkId={frameworkId}
            onOpenCompetitor={(id) => { setCompetitorId(id); setCurrentPage('competitive') }}
          />
        )}
        {currentPage === 'orgmanagement' && (
          <OrgManagement
            session={session}
            profile={effectiveProfile}
            onNavigate={setCurrentPage}
            orgUsers={orgUsers}
          />
        )}
        {currentPage === 'orgsettings' && (
          <OrganisationSettings
            session={session}
            profile={effectiveProfile}
            onNavigate={setCurrentPage}
            orgUsers={orgUsers}
            onSettingsSaved={async () => {
              await loadProfile(session.user.id)
              if (activeOrg) {
                const { data } = await supabase.from('organisations').select('*').eq('id', activeOrg.id).single()
                if (data) { setActiveOrg(data); localStorage.setItem('cadence_active_org', JSON.stringify(data)) }
              }
            }}
          />
        )}
        {currentPage === 'licence' && (
          <LicenceSettings
            session={session}
            profile={effectiveProfile}
            onNavigate={setCurrentPage}
          />
        )}
        {currentPage === 'branding' && (
          <BrandingSettings
            session={session}
            profile={effectiveProfile}
            onNavigate={setCurrentPage}
            onBrandingUpdated={async () => {
              await loadProfile(session.user.id)
              if (activeOrg) {
                const { data } = await supabase
                  .from('organisations')
                  .select('id, name, slug, primary_colour, secondary_colour, logo_url, powered_by_cadence, licensed_user_count, licence_active')
                  .eq('id', activeOrg.id)
                  .single()
                if (data) setActiveOrg(data)
              }
            }}
          />
        )}
        {currentPage === 'changepassword' && (
          <ChangePassword
            onNavigate={setCurrentPage}
          />
        )}
      </div>
    </div>
  )
}

export default App
